import React, { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { 
  Bot, Search, User, Building, Phone, Globe, FileText, 
  CheckCircle, XCircle, AlertTriangle, Clock, Shield,
  Save, Play, RefreshCw, Trash2, Pause, AlertOctagon,
  ChevronDown, ChevronUp, History, ExternalLink
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Industry playbook options
const INDUSTRIES = [
  { value: "dental", label: "Dental Practice" },
  { value: "hvac", label: "HVAC Services" },
  { value: "medspa", label: "Medical Spa" },
  { value: "veterinary", label: "Veterinary Clinic" },
  { value: "auto_repair", label: "Auto Repair" },
  { value: "home_services", label: "Home Services" },
  { value: "legal", label: "Legal Services" },
  { value: "real_estate", label: "Real Estate" },
  { value: "healthcare", label: "Healthcare" },
  { value: "restaurant", label: "Restaurant" },
  { value: "other", label: "Other" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HST)" },
  { value: "UTC", label: "UTC" },
];

export function AgentLinkingPage() {
  const { adminKey } = useOutletContext();
  const [searchParams] = useSearchParams();
  
  // State
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientContext, setClientContext] = useState(null);
  const [existingAgent, setExistingAgent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    agent_id: "",
    sip_number: "",
    location_name: "",
    location_id: "",
    timezone: "America/New_York",
    industry: "dental",
    forward_from: "",
    display_name: "",
    notes: ""
  });
  
  const [validation, setValidation] = useState({ valid: true, errors: [], warnings: [] });
  const [submitting, setSubmitting] = useState(false);
  const [changelog, setChangelog] = useState([]);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showDangerZone, setShowDangerZone] = useState(false);
  const [dangerAction, setDangerAction] = useState(null);
  const [dangerReason, setDangerReason] = useState("");

  // Load clients on mount
  useEffect(() => {
    fetchClients();
    
    // Check URL params for pre-selected client or agent
    const clientId = searchParams.get("client");
    const agentId = searchParams.get("agent");
    
    if (agentId) {
      loadExistingAgent(agentId);
    } else if (clientId) {
      // Will be selected after clients load
      setTimeout(() => selectClientById(clientId), 500);
    }
  }, [adminKey]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/clients?limit=500`, {
        headers: { "X-Admin-Key": adminKey }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const selectClientById = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      handleSelectClient(client);
    }
  };

  const loadExistingAgent = async (agentId) => {
    try {
      const res = await fetch(`${API_URL}/api/agents/${agentId}`, {
        headers: { "X-Admin-Key": adminKey }
      });
      if (res.ok) {
        const agent = await res.json();
        setExistingAgent(agent);
        setIsEditing(true);
        
        // Load form data from existing agent
        setFormData({
          agent_id: agent.agent_id,
          sip_number: agent.sip_number || "",
          location_name: agent.location_name || "",
          location_id: agent.location_id || "",
          timezone: agent.timezone || "America/New_York",
          industry: agent.industry || "dental",
          forward_from: agent.forward_from || "",
          display_name: agent.display_name || "",
          notes: agent.notes || ""
        });
        
        // Load client context
        if (agent.client_id) {
          loadClientContext(agent.client_id);
          loadChangelog(agentId);
        }
      }
    } catch (err) {
      console.error("Failed to load agent:", err);
    }
  };

  const loadClientContext = async (clientId) => {
    try {
      const res = await fetch(`${API_URL}/api/agents/client-context/${clientId}`, {
        headers: { "X-Admin-Key": adminKey }
      });
      if (res.ok) {
        const context = await res.json();
        setClientContext(context);
      }
    } catch (err) {
      console.error("Failed to load client context:", err);
    }
  };

  const loadChangelog = async (agentId) => {
    try {
      const res = await fetch(`${API_URL}/api/agents/${agentId}/changelog?limit=20`, {
        headers: { "X-Admin-Key": adminKey }
      });
      if (res.ok) {
        const logs = await res.json();
        setChangelog(logs);
      }
    } catch (err) {
      console.error("Failed to load changelog:", err);
    }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setClientContext(null);
    setExistingAgent(null);
    setIsEditing(false);
    setChangelog([]);
    
    // Reset form but keep timezone and industry
    setFormData(prev => ({
      ...prev,
      agent_id: "",
      sip_number: "",
      location_name: "",
      location_id: "",
      forward_from: "",
      display_name: "",
      notes: ""
    }));
    
    // Load client context
    await loadClientContext(client.id);
    
    // Check if client already has agents
    try {
      const res = await fetch(`${API_URL}/api/agents/by-client/${client.id}`, {
        headers: { "X-Admin-Key": adminKey }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.agents && data.agents.length > 0) {
          // Load first agent for editing
          const agent = data.agents[0];
          setExistingAgent(agent);
          setIsEditing(true);
          setFormData({
            agent_id: agent.agent_id,
            sip_number: agent.sip_number || "",
            location_name: agent.location_name || "",
            location_id: agent.location_id || "",
            timezone: agent.timezone || "America/New_York",
            industry: agent.industry || "dental",
            forward_from: agent.forward_from || "",
            display_name: agent.display_name || "",
            notes: agent.notes || ""
          });
          loadChangelog(agent.agent_id);
        }
      }
    } catch (err) {
      console.error("Failed to check existing agents:", err);
    }
  };

  // Validation
  const validateForm = useCallback(async () => {
    if (!selectedClient || !formData.agent_id) {
      setValidation({ valid: false, errors: [], warnings: [] });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/agents/validate`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          agent_id: formData.agent_id,
          client_id: selectedClient.id,
          location_id: formData.location_id || null,
          location_name: formData.location_name || null,
          industry: formData.industry,
          sip_number: formData.sip_number,
          forward_from: formData.forward_from || null,
          timezone: formData.timezone,
          display_name: formData.display_name || null,
          notes: formData.notes || null
        })
      });

      if (res.ok) {
        const result = await res.json();
        // If editing, filter out "already exists" errors for the same agent
        if (isEditing && existingAgent) {
          result.errors = result.errors.filter(e => 
            !e.includes(formData.agent_id) || !e.includes("already linked")
          );
        }
        setValidation(result);
      }
    } catch (err) {
      console.error("Validation failed:", err);
    }
  }, [selectedClient, formData, adminKey, isEditing, existingAgent]);

  // Debounced validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.agent_id && formData.sip_number) {
        validateForm();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.agent_id, formData.sip_number, validateForm]);

  // Form submission
  const handleSubmit = async (action = "save") => {
    if (!selectedClient) return;
    
    setSubmitting(true);
    
    try {
      const payload = {
        agent_id: formData.agent_id.trim(),
        client_id: selectedClient.id,
        location_id: formData.location_id.trim() || null,
        location_name: formData.location_name.trim() || null,
        industry: formData.industry,
        sip_number: formData.sip_number.trim(),
        forward_from: formData.forward_from.trim() || null,
        timezone: formData.timezone,
        display_name: formData.display_name.trim() || null,
        notes: formData.notes.trim() || null
      };

      let res;
      if (isEditing && existingAgent) {
        // Update existing agent
        res = await fetch(`${API_URL}/api/agents/${existingAgent.agent_id}`, {
          method: "PATCH",
          headers: {
            "X-Admin-Key": adminKey,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ ...payload, reason: "Admin update via Agent Linking UI" })
        });
      } else {
        // Create new agent
        res = await fetch(`${API_URL}/api/agents`, {
          method: "POST",
          headers: {
            "X-Admin-Key": adminKey,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });
      }

      if (res.ok) {
        const agent = await res.json();
        setExistingAgent(agent);
        setIsEditing(true);
        loadChangelog(agent.agent_id);
        
        // Handle additional actions
        if (action === "save_in_review") {
          await setClientInReview(agent.agent_id);
        }
        
        alert(isEditing ? "Agent updated successfully!" : "Agent created successfully!");
      } else {
        const err = await res.json();
        alert(`Error: ${JSON.stringify(err.detail || err)}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const setClientInReview = async (agentId) => {
    try {
      await fetch(`${API_URL}/api/agents/${agentId}/set-client-in-review?reason=Agent%20linking%20complete`, {
        method: "POST",
        headers: { "X-Admin-Key": adminKey }
      });
      loadClientContext(selectedClient.id);
    } catch (err) {
      console.error("Failed to set client in review:", err);
    }
  };

  // Danger zone actions
  const executeDangerAction = async () => {
    if (!existingAgent || !dangerReason.trim()) return;
    
    setSubmitting(true);
    try {
      const endpoint = dangerAction === "suspend" 
        ? `${API_URL}/api/agents/${existingAgent.agent_id}/suspend?reason=${encodeURIComponent(dangerReason)}`
        : dangerAction === "unsuspend"
        ? `${API_URL}/api/agents/${existingAgent.agent_id}/unsuspend?reason=${encodeURIComponent(dangerReason)}`
        : `${API_URL}/api/agents/${existingAgent.agent_id}/unlink?reason=${encodeURIComponent(dangerReason)}`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "X-Admin-Key": adminKey }
      });

      if (res.ok) {
        const result = await res.json();
        alert(result.message);
        
        if (dangerAction === "unlink") {
          // Reset form after unlinking
          setExistingAgent(null);
          setIsEditing(false);
          setFormData({
            agent_id: "",
            sip_number: "",
            location_name: "",
            location_id: "",
            timezone: "America/New_York",
            industry: "dental",
            forward_from: "",
            display_name: "",
            notes: ""
          });
        } else {
          // Reload agent data
          loadExistingAgent(existingAgent.agent_id);
        }
        
        setDangerAction(null);
        setDangerReason("");
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter clients by search
  const filteredClients = clients.filter(c => 
    !searchQuery || 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.id?.includes(searchQuery)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Agent Linking</h1>
          <p className="text-gray-500 text-sm mt-1">
            Connect SigmaMind agents to Calyro clients
          </p>
        </div>
        <button 
          onClick={fetchClients}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Customer Search + Context */}
        <div className="col-span-4 space-y-4">
          {/* Search */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Search Customer
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Business name, email, or ID..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent"
              />
            </div>
            
            {/* Client List */}
            <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
              {filteredClients.slice(0, 10).map(client => (
                <button
                  key={client.id}
                  onClick={() => handleSelectClient(client)}
                  className={`w-full text-left p-2 rounded-lg transition-all ${
                    selectedClient?.id === client.id 
                      ? "bg-[#4F80F5] text-white shadow-md" 
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <div className="font-medium truncate">{client.name || "Unnamed"}</div>
                  <div className="text-xs opacity-70 truncate">{client.id?.slice(0, 16)}...</div>
                </button>
              ))}
              {filteredClients.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-2">No clients found</p>
              )}
            </div>
          </div>

          {/* Customer Context Card */}
          {clientContext && (
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3 shadow-sm">
              <h3 className="text-gray-800 font-semibold flex items-center gap-2">
                <User className="w-4 h-4 text-[#4F80F5]" />
                {clientContext.name}
              </h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Plan</span>
                  <span className="text-gray-800 font-medium">{clientContext.plan_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Onboarding</span>
                  <StatusBadge status={clientContext.onboarding_status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Billing</span>
                  <StatusBadge status={clientContext.billing_status} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Setup Fee</span>
                  <span className={clientContext.setup_fee_paid ? "text-green-600" : "text-orange-500"}>
                    {clientContext.setup_fee_paid ? "Paid" : "Unpaid"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Locations</span>
                  <span className={`font-medium ${
                    clientContext.locations_available === 0 ? "text-red-500" : "text-gray-800"
                  }`}>
                    {clientContext.locations_used}/{clientContext.max_locations || "∞"}
                    {clientContext.locations_available > 0 && (
                      <span className="text-green-600 ml-1">
                        ({clientContext.locations_available} available)
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {clientContext.onboarding_status !== "live" && (
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-yellow-700 text-xs">
                    ⚠️ Client not yet live. Calls will be blocked until status is &quot;live&quot;.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: Agent Form */}
        <div className="col-span-8 space-y-4">
          {/* Main Form */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-gray-800 font-semibold flex items-center gap-2">
                <Bot className="w-5 h-5 text-[#4F80F5]" />
                {isEditing ? "Edit Agent Mapping" : "Create Agent Mapping"}
              </h3>
              {existingAgent && (
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  existingAgent.status === "active" ? "bg-green-100 text-green-700" :
                  existingAgent.status === "suspended" ? "bg-red-100 text-red-700" :
                  "bg-gray-100 text-gray-600"
                }`}>
                  {existingAgent.status}
                </span>
              )}
            </div>

            {!selectedClient ? (
              <div className="text-center py-8 text-gray-400">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a customer to begin</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Required Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="SigmaMind Agent ID"
                    icon={Bot}
                    required
                    value={formData.agent_id}
                    onChange={(v) => setFormData(f => ({ ...f, agent_id: v }))}
                    placeholder="sig_abc123xyz"
                    hint="The agent ID from SigmaMind platform"
                    disabled={isEditing}
                  />
                  <FormField
                    label="SIP Number"
                    icon={Phone}
                    required
                    value={formData.sip_number}
                    onChange={(v) => setFormData(f => ({ ...f, sip_number: v }))}
                    placeholder="+1-555-123-4567"
                    hint="Phone number assigned to this agent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Location Name"
                    icon={Building}
                    required
                    value={formData.location_name}
                    onChange={(v) => setFormData(f => ({ ...f, location_name: v }))}
                    placeholder="Dallas - Main Office"
                    hint="Human-readable location name"
                  />
                  <FormSelect
                    label="Timezone"
                    icon={Globe}
                    required
                    value={formData.timezone}
                    onChange={(v) => setFormData(f => ({ ...f, timezone: v }))}
                    options={TIMEZONES}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormSelect
                    label="Industry Playbook"
                    icon={FileText}
                    required
                    value={formData.industry}
                    onChange={(v) => setFormData(f => ({ ...f, industry: v }))}
                    options={INDUSTRIES}
                  />
                  <FormField
                    label="Forward-From Number"
                    icon={Phone}
                    value={formData.forward_from}
                    onChange={(v) => setFormData(f => ({ ...f, forward_from: v }))}
                    placeholder="+1-555-987-6543"
                    hint="Customer's existing number (optional)"
                  />
                </div>

                {/* Optional Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Location ID"
                    icon={Building}
                    value={formData.location_id}
                    onChange={(v) => setFormData(f => ({ ...f, location_id: v }))}
                    placeholder="loc_dallas_main"
                    hint="Internal location identifier"
                  />
                  <FormField
                    label="Display Name"
                    icon={User}
                    value={formData.display_name}
                    onChange={(v) => setFormData(f => ({ ...f, display_name: v }))}
                    placeholder="Main Reception Line"
                    hint="Friendly name for dashboard"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-gray-700 text-sm mb-1">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Any additional notes for this agent..."
                    rows={2}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent resize-none"
                  />
                </div>

                {/* Validation Messages */}
                {(validation.errors.length > 0 || validation.warnings.length > 0) && (
                  <div className="space-y-2">
                    {validation.errors.map((err, i) => (
                      <div key={i} className="p-2 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm flex items-start gap-2">
                        <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {err}
                      </div>
                    ))}
                    {validation.warnings.map((warn, i) => (
                      <div key={i} className="p-2 bg-yellow-900/50 border border-yellow-700 rounded-lg text-yellow-300 text-sm flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {warn}
                      </div>
                    ))}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleSubmit("save")}
                    disabled={submitting || validation.errors.length > 0 || !formData.agent_id || !formData.sip_number}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#6EDBC5] text-white rounded-lg hover:bg-[#5DCDB7] font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    <Save className="w-4 h-4" />
                    {submitting ? "Saving..." : "Save Mapping"}
                  </button>
                  <button
                    onClick={() => handleSubmit("save_in_review")}
                    disabled={submitting || validation.errors.length > 0 || !formData.agent_id || !formData.sip_number}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-[#4F80F5] text-white rounded-lg hover:bg-[#3D6FE4] font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                  >
                    <Play className="w-4 h-4" />
                    Save + Set In Review
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Audit/Changelog Section */}
          {existingAgent && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setShowChangelog(!showChangelog)}
                className="w-full p-4 flex items-center justify-between text-gray-800 hover:bg-gray-50"
              >
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-[#4F80F5]" />
                  <span className="font-semibold">Audit & Change Log</span>
                  <span className="text-gray-500 text-sm">({changelog.length} entries)</span>
                </div>
                {showChangelog ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showChangelog && (
                <div className="border-t border-gray-200 p-4 max-h-64 overflow-y-auto bg-gray-50">
                  {changelog.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-4">No changes recorded yet</p>
                  ) : (
                    <div className="space-y-3">
                      {changelog.map((log, i) => (
                        <div key={log.id || i} className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-gray-800 font-medium capitalize">{log.action.replace(/_/g, " ")}</span>
                            <span className="text-gray-400 text-xs">
                              {new Date(log.timestamp).toLocaleString()}
                            </span>
                          </div>
                          {log.reason && (
                            <p className="text-gray-600 text-sm">{log.reason}</p>
                          )}
                          {log.changes && Object.keys(log.changes).length > 0 && (
                            <div className="mt-2 text-xs text-gray-500">
                              {Object.entries(log.changes).map(([key, val]) => (
                                <div key={key}>
                                  <span className="text-gray-400">{key}:</span> 
                                  {typeof val === "object" && val.from !== undefined
                                    ? <span> {String(val.from)} → {String(val.to)}</span>
                                    : <span> {String(val)}</span>
                                  }
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="mt-1 text-xs text-gray-400">
                            by {log.performed_by || "admin"}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Danger Zone */}
          {existingAgent && (
            <div className="bg-white rounded-xl border border-red-200 overflow-hidden shadow-sm">
              <button
                onClick={() => setShowDangerZone(!showDangerZone)}
                className="w-full p-4 flex items-center justify-between text-gray-800 hover:bg-red-50"
              >
                <div className="flex items-center gap-2">
                  <AlertOctagon className="w-5 h-5 text-red-400" />
                  <span className="font-semibold text-red-400">Danger Zone</span>
                </div>
                {showDangerZone ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {showDangerZone && (
                <div className="border-t border-red-200 p-4 space-y-4 bg-red-50">
                  {!dangerAction ? (
                    <div className="flex gap-3">
                      {existingAgent.status === "suspended" ? (
                        <button
                          onClick={() => setDangerAction("unsuspend")}
                          className="flex items-center gap-2 px-4 py-2 bg-green-100 border border-green-300 text-green-700 rounded-lg hover:bg-green-200"
                        >
                          <Play className="w-4 h-4" />
                          Unsuspend Agent
                        </button>
                      ) : (
                        <button
                          onClick={() => setDangerAction("suspend")}
                          className="flex items-center gap-2 px-4 py-2 bg-yellow-100 border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-200"
                        >
                          <Pause className="w-4 h-4" />
                          Suspend Agent
                        </button>
                      )}
                      <button
                        onClick={() => setDangerAction("unlink")}
                        className="flex items-center gap-2 px-4 py-2 bg-red-100 border border-red-300 text-red-700 rounded-lg hover:bg-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        Unlink Agent
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-3 bg-red-100 rounded-lg border border-red-200">
                        <p className="text-red-700 font-medium">
                          {dangerAction === "suspend" && "⚠️ Suspending will immediately block all calls to this agent."}
                          {dangerAction === "unsuspend" && "✓ Unsuspending will restore the agent to active status."}
                          {dangerAction === "unlink" && "🗑️ Unlinking will permanently delete this agent mapping."}
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-gray-700 text-sm mb-1">
                          Reason (required for audit)
                        </label>
                        <input
                          type="text"
                          value={dangerReason}
                          onChange={(e) => setDangerReason(e.target.value)}
                          placeholder="Enter reason..."
                          className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <button
                          onClick={() => { setDangerAction(null); setDangerReason(""); }}
                          className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={executeDangerAction}
                          disabled={!dangerReason.trim() || submitting}
                          className={`flex-1 py-2 rounded-lg font-semibold disabled:opacity-50 ${
                            dangerAction === "unlink" 
                              ? "bg-red-600 text-white hover:bg-red-700"
                              : dangerAction === "unsuspend"
                              ? "bg-green-600 text-white hover:bg-green-700"
                              : "bg-yellow-500 text-white hover:bg-yellow-600"
                          }`}
                        >
                          {submitting ? "Processing..." : `Confirm ${dangerAction}`}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Form Components
function FormField({ label, icon: Icon, required, value, onChange, placeholder, hint, disabled }) {
  return (
    <div>
      <label className="block text-gray-700 text-sm mb-1">
        {label} {required && <span className="text-[#4F80F5]">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </div>
      {hint && <p className="text-gray-500 text-xs mt-1">{hint}</p>}
    </div>
  );
}

function FormSelect({ label, icon: Icon, required, value, onChange, options }) {
  return (
    <div>
      <label className="block text-gray-700 text-sm mb-1">
        {label} {required && <span className="text-[#4F80F5]">*</span>}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent appearance-none"
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const getStyle = () => {
    switch (status) {
      case "live": case "active": return "bg-green-100 text-green-700";
      case "in_review": return "bg-blue-100 text-blue-700";
      case "pending_setup": return "bg-yellow-100 text-yellow-700";
      case "suspended": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStyle()}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

export default AgentLinkingPage;
